import type { CalloutMessages, SelectOption } from '@camelot/components'
import * as Sentry from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import { defineStore } from 'pinia'
import { reactive, ref } from 'vue'
import { ExternalApi } from '~/api/location'
import type { CheckPositionIsInBusinessAreaRequest, LocationCheckResult } from '~/api/location'
import type { GetLocationConfigRequest, PrebookingUiLocationConfig } from '~/api/prebooking'
import { ExternalControllerApi } from '~/api/prebooking/apis/ExternalControllerApi'
import { BERLIN } from '~/composables/city'
import type { City } from '~/composables/city'
import { useGoogleAPI } from '~/composables/google-api'
import { translate } from '~/composables/translate/translations'
import type { Address } from '~/types/location'
import LatLng = google.maps.LatLng

// @ts-expect-error Say thanks Google Inc for such a beautiful code
import GeocoderRequest = google.maps.GeocoderRequest

const DEBOUNCING_INPUT_ADDRESS_VALIDATION_TIME_MS = 200
const locationAPI = new ExternalApi()
const prebookingApi = new ExternalControllerApi()

export const useLocationStore = defineStore('location', () => {
  const city = ref<City>(BERLIN)
  const address = ref<Address | undefined>(undefined)
  const addressOptions = reactive<Set<SelectOption>>(new Set<SelectOption>())
  const addressError = ref<CalloutMessages | undefined>(undefined)
  const prebookingLocationConfig = ref<PrebookingUiLocationConfig>()

  const validateAddressInput = useDebounceFn(isValidAddress, DEBOUNCING_INPUT_ADDRESS_VALIDATION_TIME_MS)

  watchEffect(async () => {
    try {
      // isValid can be null if the Promise is cancelled by the bouncer - for example if another request comes in
      const { geoCoderService } = useGoogleAPI()
      const isValid: boolean | undefined = await validateAddressInput(city.value, address.value, geoCoderService.value)
      addressError.value = isValid === false ? { error: translate('api_errors_nothomearea') } : undefined
    }
    catch (error) {
      Sentry.captureException(error)
      console.error('Error while checking address', error)
    }
  })

  const isValid = computed<boolean>(() => !!address.value && !addressError.value)

  async function getLocationConfig(): Promise<PrebookingUiLocationConfig> {
    const rq: GetLocationConfigRequest = { locationId: city.value.locationId }
    const result: PrebookingUiLocationConfig = await prebookingApi.getLocationConfig(rq)
    prebookingLocationConfig.value = { ...result }
    return result
  }
  return {
    city,
    address,
    addressOptions,
    addressError,
    isValid,
    getLocationConfig,
    prebookingLocationConfig,
  }
})

async function isPositionInBusinessArea(request: CheckPositionIsInBusinessAreaRequest): Promise<boolean> {
  const result: LocationCheckResult = await locationAPI.checkPositionIsInBusinessArea(request)
  return result.inZone ? result.inZone : false
}

function isValidAddress(city: City, address?: Address, geoCoderService?: google.maps.Geocoder): Promise<boolean> {
  if (!address)
    return Promise.resolve(true)

  if (address.location) {
    return checkAirportLocation(address, city)
  }
  else if (geoCoderService) {
    return checkAddress(address, city, geoCoderService)
  }
  else {
    // "Normal" address but GeoCoderService not available
    return Promise.resolve(true)
  }
}

function checkAirportLocation(address: Address, city: City) {
  const re: CheckPositionIsInBusinessAreaRequest = {
    lcRequestRto: {
      position: { latitude: address.location!.latitude, longitude: address.location!.longitude },
      locationId: city.locationId,
    },
  }
  return isPositionInBusinessArea(re)
}

async function checkAddress(address: Address, city: City, geoCoderService: google.maps.Geocoder) {
  const request: GeocoderRequest = { address: address.info, componentRestrictions: { country: city.country } }
  const result = await geoCoderService.geocode(request)
  const location: LatLng = result.results[0].geometry.location

  const position = { latitude: location.lat(), longitude: location.lng() }

  if (!address.location)
    address.location = position

  const rq: CheckPositionIsInBusinessAreaRequest = { lcRequestRto: { position, locationId: city.locationId } }
  return isPositionInBusinessArea(rq)
}
